import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import _ from 'lodash';
import styles from './MobileCarouselSlider.module.scss';
import { useCarousel } from './Carousel';

const GAP = 24;

interface MobileCarouselSliderProps {
  maxHeight?: number;
}

const MobileCarouselSlider = ({ maxHeight }: MobileCarouselSliderProps) => {
  const { images, dispatch, currentIndex } = useCarousel();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const targetIndexRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!containerRef.current) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (
      !containerRef.current ||
      containerWidth == null ||
      currentIndex === targetIndexRef.current
    )
      return;

    targetIndexRef.current = currentIndex;
    const offset = (containerWidth + GAP) * currentIndex;
    containerRef.current.scrollTo({
      left: offset,
      behavior: 'smooth',
    });
  }, [currentIndex, containerWidth]);

  return (
    <div
      className={styles.container}
      ref={containerRef}
      style={{ maxHeight, gap: GAP }}
      onScroll={(e) => {
        if (containerWidth == null) return;
        const target = e.target as HTMLDivElement;
        const index = _.round(target.scrollLeft / (containerWidth + GAP));

        if (targetIndexRef.current === index) {
          targetIndexRef.current = null;
          return;
        }

        const image = images[index];

        if (
          image == null ||
          index === currentIndex ||
          targetIndexRef.current != null
        ) {
          return;
        }

        targetIndexRef.current = index;
        dispatch({ type: 'select', image });
      }}
    >
      {containerWidth != null &&
        images.map((image, i) => {
          return (
            <img
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              alt={image.caption ?? 'User-uploaded image'}
              src={image.source}
              style={{
                minWidth: containerWidth,
                maxWidth: containerWidth,
                maxHeight,
              }}
            />
          );
        })}
    </div>
  );
};

export default MobileCarouselSlider;
